import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, pa, eur, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Bike, ShoppingBag, Clock, Phone, User, ArrowRight } from "lucide-react";
import { CapienzaForno } from "@/components/CapienzaForno";

const STATES = ["nuovo", "in_preparazione", "pronto", "ritirato", "consegnato"];
const COLOR = {
  nuovo: "border-slate-300 bg-white",
  in_preparazione: "border-amber-500 bg-amber-50",
  pronto: "border-emerald-500 bg-emerald-50",
  ritirato: "border-slate-200 bg-slate-50",
  consegnato: "border-slate-200 bg-slate-50",
};

export default function Asporto() {
  const { user, settings, refresh } = useApp();
  const { t } = useI18n();
  const nav = useNavigate();
  const [channels, setChannels] = useState([]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: "glovo", customer_name: "", phone: "", pickup_time: "", pezzi: "", rider: "", external_code: "", address: "", note: "" });
  const [orario, setOrario] = useState(null);
  const [ultimo, setUltimo] = useState(null);

  const load = useCallback(async () => {
    const [c, o, s] = await Promise.all([g("/channels"), g("/takeaway"), g("/takeaway/summary")]);
    setChannels(c);
    setOrders(o);
    setSummary(s);
  }, []);
  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  // L'ora la propone il cliente: l'app risponde sì oppure offre le prime fasce libere.
  useEffect(() => {
    if (!form.pickup_time) { setOrario(null); return; }
    const timer = setTimeout(async () => {
      try {
        setOrario(await g(`/takeaway/orario?ora=${form.pickup_time}&pezzi=${Number(form.pezzi) || 1}`));
      } catch { setOrario(null); }
    }, 300);
    return () => clearTimeout(timer);
  }, [form.pickup_time, form.pezzi]);

  // Numero riconosciuto: proponiamo l'ordine di sempre, un tocco e l'hai preso.
  useEffect(() => {
    const numero = (form.phone || "").replace(/\D/g, "");
    if (numero.length < 6) { setUltimo(null); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await g(`/takeaway/ultimo?phone=${encodeURIComponent(form.phone)}`);
        setUltimo(r.trovato ? r : null);
      } catch { setUltimo(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.phone]);

  const create = async (righe) => {
    try {
      const o = await p("/takeaway", { ...form, pezzi: Number(form.pezzi) || 0, waiter: user.name, items: righe || [] });
      toast.success(t("as.created", { n: o.table_numbers[0] }), { description: t("as.createdSub") });
      setOpen(false);
      setForm({ ...form, customer_name: "", phone: "", pickup_time: "", pezzi: "", rider: "", external_code: "", address: "", note: "" });
      setUltimo(null);
      setOrario(null);
      nav(`/comanda/${o.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setStatus = async (o, status) => {
    try {
      await pa(`/takeaway/${o.id}/status`, { status, by: user.name });
      toast.success(`${o.table_numbers[0]}: ${t(`as.st.${status}`).toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = orders.filter((o) => filter === "tutti" || o.channel === filter);
  const isDelivery = (ch) => channels.find((c) => c.id === ch)?.kind === "delivery";

  return (
    <div className="fade-up space-y-5" data-testid="asporto-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("as.overline")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("as.title")}</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-11" data-testid="new-takeaway-btn">
              <Plus className="mr-1.5 h-4 w-4" /> {t("as.newTitle")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="takeaway-dialog">
            <DialogHeader>
              <DialogTitle>{t("as.newTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("as.channel")}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {channels.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`channel-${c.id}`}
                      onClick={() => setForm({ ...form, channel: c.id })}
                      className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                        form.channel === c.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {c.kind === "delivery" ? <Bike className="h-3.5 w-3.5" /> : <ShoppingBag className="h-3.5 w-3.5" />}
                      {c.name}
                      {c.fee_pct > 0 && <span className="font-num text-[10px] opacity-70">-{c.fee_pct}%</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder={t("as.customer")} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="takeaway-name-input" />
                <Input placeholder={t("as.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="takeaway-phone-input" />
                <Input type="time" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} data-testid="takeaway-time-input" />
                <Input type="number" min="0" placeholder="quante pizze" value={form.pezzi} onChange={(e) => setForm({ ...form, pezzi: e.target.value })} data-testid="takeaway-pezzi-input" />
                <Input placeholder={t("as.extCode")} value={form.external_code} onChange={(e) => setForm({ ...form, external_code: e.target.value })} data-testid="takeaway-code-input" />
              </div>

              {ultimo && (
                <button
                  data-testid="takeaway-ultimo-btn"
                  onClick={() => create(ultimo.righe.map((r) => ({ menu_item_id: r.menu_item_id, qty: r.qty, note: r.note, extras: r.extras, removed: r.removed })))}
                  className="w-full rounded-lg border-2 border-emerald-500 bg-emerald-50 px-3 py-3 text-left transition-colors hover:bg-emerald-100"
                >
                  <p className="text-sm font-bold text-emerald-900">Come l'ultima volta</p>
                  <p className="text-xs text-emerald-800">
                    {ultimo.cliente ? `${ultimo.cliente} · ` : ""}{ultimo.pezzi} pezzi, {eur(ultimo.totale)} · ultimo ordine il{" "}
                    {ultimo.quando.slice(8, 10)}/{ultimo.quando.slice(5, 7)}
                  </p>
                  <p className="mt-1 truncate text-[11px] text-emerald-700">
                    {ultimo.righe.map((r) => `${r.qty}× ${r.name}${r.note ? ` (${r.note})` : ""}`).join(", ")}
                  </p>
                </button>
              )}

              {orario && (
                <div
                  data-testid="takeaway-orario-esito"
                  className={`rounded-lg px-3 py-2.5 text-sm ${orario.ok ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`}
                >
                  {orario.ok ? (
                    <p className="font-semibold">
                      Le {form.pickup_time} vanno bene
                      {orario.libere != null && <span className="font-normal"> · restano {orario.libere} pizze su {orario.capienza} in quella fascia</span>}
                    </p>
                  ) : (
                    <>
                      <p className="font-semibold">Alle {form.pickup_time} non ce la fai: {orario.motivo}</p>
                      {orario.alternative?.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-xs">Proponi al cliente:</span>
                          {orario.alternative.map((a) => (
                            <button
                              key={a}
                              data-testid={`takeaway-alt-${a.replace(":", "")}`}
                              onClick={() => setForm({ ...form, pickup_time: a })}
                              className="font-num rounded-full bg-white px-3 py-1 text-sm font-bold ring-1 ring-amber-300"
                            >
                              {a}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {isDelivery(form.channel) && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t("as.rider")} value={form.rider} onChange={(e) => setForm({ ...form, rider: e.target.value })} data-testid="takeaway-rider-input" />
                  <Input placeholder={t("as.address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="takeaway-address-input" />
                </div>
              )}
              <Input placeholder={t("as.notePh")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="takeaway-note-input" />
              <Button className="h-12 w-full" onClick={() => create()} data-testid="create-takeaway-btn">
                {t("as.create")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="takeaway-summary">
          {[["orders", t("as.ordersToday"), summary.orders], ["gross", t("as.gross"), eur(summary.gross)],
            ["fees", t("as.fees"), eur(summary.fee)], ["net", t("as.net"), eur(summary.net)]].map(([id, l, v]) => (
            <Card key={id} className="p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
              <p className="font-num mt-2 text-xl font-bold sm:text-2xl">{v}</p>
            </Card>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("tutti")}
          data-testid="filter-tutti"
          className={`rounded-md px-3 py-2 text-sm font-semibold ${filter === "tutti" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}
        >
          {t("as.all")}
        </button>        {channels.map((c) => (
          <button
            key={c.id}
            onClick={() => setFilter(c.id)}
            data-testid={`filter-${c.id}`}
            className={`rounded-md px-3 py-2 text-sm font-semibold ${filter === c.id ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {shown.length === 0 && <p className="text-sm text-slate-500">{t("as.none")}</p>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((o) => (
          <Card key={o.id} className={`border-2 p-5 ${COLOR[o.delivery_status] || COLOR.nuovo}`} data-testid={`takeaway-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-num text-lg font-bold">{o.table_numbers[0]}</p>
                <p className="text-xs font-semibold text-slate-600">{o.channel_name}</p>
              </div>
              <div className="text-right">
                <p className="font-num text-lg font-bold">{eur(o.total)}</p>
                {o.fee_pct > 0 && (
                  <p className="font-num text-[11px] text-rose-600">
                    -{o.fee_pct}% = {eur(o.net_total)}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 space-y-1 text-xs text-slate-600">
              {o.customer_name && (
                <p className="flex items-center gap-1.5">
                  <User className="h-3 w-3" /> {o.customer_name}
                </p>
              )}
              {o.phone && (
                <p className="flex items-center gap-1.5">
                  <Phone className="h-3 w-3" /> {o.phone}
                </p>
              )}
              {o.pickup_time && (
                <p className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> ritiro {o.pickup_time}
                </p>
              )}
              {o.rider && (
                <p className="flex items-center gap-1.5">
                  <Bike className="h-3 w-3" /> rider {o.rider}
                </p>
              )}
              {o.address && <p className="truncate">{o.address}</p>}
              {o.note && <p className="text-amber-700">{o.note}</p>}
              <p>{t("as.itemsLine", { n: o.items.length, sent: o.items.filter((i) => i.sent).length })}</p>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {STATES.map((s) => (
                <button
                  key={s}
                  data-testid={`takeaway-status-${o.id}-${s}`}
                  onClick={() => setStatus(o, s)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    o.delivery_status === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"
                  }`}
                >
                  {t(`as.st.${s}`)}
                </button>
              ))}
            </div>

            {o.status === "aperto" && (
              <Button className="mt-4 h-11 w-full" data-testid={`open-takeaway-${o.id}`} onClick={() => nav(`/comanda/${o.id}`)}>
                {t("as.openOrder")}
              </Button>
            )}
          </Card>
        ))}
      </div>

      {(user?.role === "titolare" || user?.role === "direttore") && (
        <CapienzaForno settings={settings} onSalva={refresh} />
      )}

      {summary?.per_channel?.length > 0 && (        <Card className="p-5" data-testid="channel-table">
          <h3 className="text-lg font-semibold">{t("as.perChannel")}</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                  <th className="py-2 font-bold">{t("as.channel")}</th>
                  <th className="py-2 font-bold">{t("as.chOrders")}</th>
                  <th className="py-2 font-bold">{t("sr.gross")}</th>
                  <th className="py-2 font-bold">{t("as.chFee")}</th>
                  <th className="py-2 text-right font-bold">{t("as.chNet")}</th>
                </tr>
              </thead>
              <tbody>
                {summary.per_channel.map((r) => (
                  <tr key={r.channel} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 font-medium">{r.channel}</td>
                    <td className="py-2.5 font-num">{r.orders}</td>
                    <td className="py-2.5 font-num">{eur(r.gross)}</td>
                    <td className="py-2.5 font-num text-rose-600">-{eur(r.fee)} ({r.fee_pct}%)</td>
                    <td className="py-2.5 text-right font-num font-semibold">{eur(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
